import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { Permission } from '@accel/shared';
import { api, setSessionEndedHandler, tokens } from '../lib/api';
import type { Session } from '../lib/types';

interface AuthState {
  session: Session | null;
  /** True until the stored token has been checked against the server. */
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  switchMembership: (membershipId: string) => Promise<void>;
  /** Open a short session as somebody else, keeping your own aside. */
  viewAs: (userId: string) => Promise<void>;
  /** Return to your own session. */
  stopViewingAs: () => void;
  can: (permission: Permission) => boolean;
}

const AuthContext = createContext<AuthState | null>(null);

const SESSION_KEY = 'accel.session';
/**
 * Where an administrator's own session waits while they view as somebody
 * else. Held separately so returning is a restore rather than a fresh login
 * — and so an impersonated session can never overwrite the real one.
 */
const REAL_SESSION_KEY = 'accel.session.real';
const REAL_TOKENS_KEY = 'accel.tokens.real';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(() => {
    // Rendering from the cached session avoids a login flash on reload; it is
    // revalidated against /auth/me immediately below.
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw || !tokens.access) return null;
    try {
      return JSON.parse(raw) as Session;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);

  const clear = useCallback(() => {
    tokens.clear();
    localStorage.removeItem(SESSION_KEY);
    // Signing out while viewing as somebody else must not leave the
    // administrator's own session sitting in storage with no way back to it.
    localStorage.removeItem(REAL_SESSION_KEY);
    localStorage.removeItem(REAL_TOKENS_KEY);
    setSession(null);
  }, []);

  useEffect(() => {
    setSessionEndedHandler(clear);
  }, [clear]);

  // The cached session says what the token USED to grant. A membership can be
  // revoked while the tab was closed, so confirm with the server before
  // rendering anything that depends on it.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!tokens.access) {
        setLoading(false);
        return;
      }
      try {
        await api.get('/auth/me');
      } catch {
        if (!cancelled) clear();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [clear]);

  const store = useCallback((next: Session) => {
    tokens.set(next.accessToken, next.refreshToken);
    localStorage.setItem(SESSION_KEY, JSON.stringify(next));
    setSession(next);
  }, []);

  const login = useCallback(
    async (email: string, password: string) => {
      const next = await api.post<Session>('/auth/login', { email, password }, { anonymous: true });
      store(next);
    },
    [store],
  );

  const logout = useCallback(async () => {
    const refreshToken = tokens.refresh;
    if (refreshToken) {
      // Best effort: a failed revoke must not trap the user in a session.
      await api.post('/auth/logout', { refreshToken }, { anonymous: true }).catch(() => {});
    }
    clear();
  }, [clear]);

  const switchMembership = useCallback(
    async (membershipId: string) => {
      const next = await api.post<Session>('/auth/switch-membership', { membershipId });
      store(next);
    },
    [store],
  );

  const viewAs = useCallback(
    async (userId: string) => {
      // Put the administrator's own session somewhere the impersonated one
      // cannot reach, before anything is replaced.
      localStorage.setItem(REAL_SESSION_KEY, localStorage.getItem(SESSION_KEY) ?? '');
      localStorage.setItem(
        REAL_TOKENS_KEY,
        JSON.stringify({ access: tokens.access, refresh: tokens.refresh }),
      );

      try {
        const next = await api.post<Session>(`/users/${userId}/impersonate`, {});
        // The server issues no refresh token for this session: it is meant to
        // expire in fifteen minutes and not be renewable.
        tokens.set(next.accessToken, '');
        localStorage.setItem(SESSION_KEY, JSON.stringify(next));
        setSession(next);
      } catch (err) {
        localStorage.removeItem(REAL_SESSION_KEY);
        localStorage.removeItem(REAL_TOKENS_KEY);
        throw err;
      }
    },
    [],
  );

  const stopViewingAs = useCallback(() => {
    const raw = localStorage.getItem(REAL_SESSION_KEY);
    const rawTokens = localStorage.getItem(REAL_TOKENS_KEY);
    localStorage.removeItem(REAL_SESSION_KEY);
    localStorage.removeItem(REAL_TOKENS_KEY);

    if (!raw || !rawTokens) {
      // Nothing to go back to — safer to end the session than to leave
      // somebody sitting in a borrowed one.
      clear();
      return;
    }

    const restored = JSON.parse(raw) as Session;
    const { access, refresh } = JSON.parse(rawTokens) as { access: string; refresh: string };
    tokens.set(access, refresh);
    localStorage.setItem(SESSION_KEY, raw);
    setSession(restored);
  }, [clear]);

  const can = useCallback(
    (permission: Permission) => session?.permissions.includes(permission) ?? false,
    [session],
  );

  const value = useMemo<AuthState>(
    () => ({ session, loading, login, logout, switchMembership, viewAs, stopViewingAs, can }),
    [session, loading, login, logout, switchMembership, viewAs, stopViewingAs, can],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside an AuthProvider');
  return ctx;
}

/** The session, asserted non-null — for use inside authenticated routes. */
export function useSession(): Session {
  const { session } = useAuth();
  if (!session) throw new Error('useSession used outside an authenticated route');
  return session;
}
